/**
 * vmx-reviews-page.js
 *
 * Bypasses Shopify's 95-block-per-section limit on /pages/reviews.
 * The Liquid template renders the first ~95 reviews from section blocks;
 * this script fetches the rest from the canonical reviews JSON and
 * appends them into the same grid, then powers "View More" pagination.
 *
 * Wired automatically — looks for [data-reviews-grid] on the page.
 * No-op if already-rendered blocks already cover the JSON total.
 */
(function () {
  'use strict';

  var SRC = '/cdn/shop/t/14/assets/vmx-reviews-data.json';
  var CHUNK_SIZE = 30;

  function init() {
    var grid = document.querySelector('[data-reviews-grid="true"]');
    if (!grid) return;

    // Discover canonical asset URL — prefer one from a PDP reviews section
    // (it has a fingerprinted ?v= cache-buster), fall back to the bare URL.
    var probe = document.querySelector('[data-reviews-src]');
    var src = probe ? probe.getAttribute('data-reviews-src') : SRC;

    var renderedIds = collectRenderedIds(grid);
    var visibleSpan = document.querySelector('[data-visible]');
    var totalSpan   = document.querySelector('[data-total]');
    var moreBtn     = document.querySelector('[data-load-more-btn="true"]');
    var moreWrap    = document.querySelector('[data-load-more="true"]');

    var allReviews = [];
    var pendingCursor = 0;

    fetch(src, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var jsonReviews = data.reviews || [];

        // Filter out reviews already in the DOM (Liquid-rendered).
        var jsonExtras = jsonReviews.filter(function (rev) {
          return !renderedIds.has(String(rev.id));
        });

        allReviews = jsonExtras;

        // Update the page's total + visible counts to reflect real totals.
        var totalAll = renderedIds.size + jsonExtras.length;
        if (totalSpan) totalSpan.textContent = totalAll;

        if (jsonExtras.length === 0) return;

        if (moreWrap) moreWrap.style.display = '';
        if (moreBtn) {
          moreBtn.addEventListener('click', function () {
            renderNextChunk();
          });
        }

        // Hide "View More" once everything's loaded; reveal current chunk now.
        renderNextChunk();
      })
      .catch(function (err) {
        console.warn('[vmx-reviews-page] Failed to load reviews JSON:', err);
      });

    function renderNextChunk() {
      var end = Math.min(pendingCursor + CHUNK_SIZE, allReviews.length);
      var html = '';
      for (var i = pendingCursor; i < end; i++) {
        html += renderCard(allReviews[i]);
      }
      grid.insertAdjacentHTML('beforeend', html);
      pendingCursor = end;

      if (visibleSpan) {
        var renderedNow = grid.querySelectorAll('.vmx-reviews-card').length;
        visibleSpan.textContent = renderedNow;
      }

      // Re-init star-rating DOM for the new cards (if a helper exists).
      if (window.vmxReviewsPaintStars) window.vmxReviewsPaintStars(grid);

      if (pendingCursor >= allReviews.length && moreBtn) {
        moreBtn.disabled = true;
        var label = moreBtn.querySelector('span') || moreBtn;
        label.textContent = 'All ' + (allReviews.length + collectRenderedIds(grid).size - allReviews.length) + ' reviews shown';
      }
    }
  }

  function collectRenderedIds(grid) {
    var set = new Set();
    grid.querySelectorAll('[data-review-id]').forEach(function (el) {
      set.add(el.getAttribute('data-review-id'));
    });
    return set;
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c];
    });
  }

  function renderCard(r) {
    var verifiedBadge = r.verified
      ? '<span class="vmx-reviews-verified">'
        + '<svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">'
        +   '<path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>'
        + '</svg> Verified'
        + '</span>'
      : '';

    return ''
      + '<article class="vmx-reviews-card" data-review-id="' + escapeHtml(r.id) + '" data-product-slug="' + escapeHtml(r.productSlug) + '" data-stars="' + r.stars + '">'
      +   '<div class="vmx-reviews-card-header">'
      +     '<div class="vmx-reviews-card-author">'
      +       '<span class="vmx-reviews-avatar" style="background-color: ' + escapeHtml(r.avatarColor || '#2e483b') + '" aria-hidden="true">' + escapeHtml(r.initials || '?') + '</span>'
      +       '<div>'
      +         '<p class="vmx-reviews-author-name">' + escapeHtml(r.author) + '</p>'
      +         '<p class="vmx-reviews-location">' + escapeHtml(r.location) + '</p>'
      +       '</div>'
      +     '</div>'
      +     verifiedBadge
      +   '</div>'
      +   '<div class="vmx-reviews-card-stars">'
      +     '<div class="vmx-reviews-stars" data-stars="' + r.stars + '" aria-label="' + r.stars + ' out of 5 stars"></div>'
      +     '<span class="vmx-reviews-card-date">' + escapeHtml(r.date) + '</span>'
      +   '</div>'
      +   '<h3 class="vmx-reviews-card-headline">' + escapeHtml(r.headline) + '</h3>'
      +   '<p class="vmx-reviews-card-body">' + escapeHtml(r.body) + '</p>'
      +   '<div class="vmx-reviews-card-footer">'
      +     '<a href="/products/' + escapeHtml(r.productSlug) + '" class="vmx-reviews-product-tag">'
      +       '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">'
      +         '<path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/>'
      +         '<path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>'
      +       '</svg> '
      +       escapeHtml(r.productLabel)
      +     '</a>'
      +   '</div>'
      + '</article>';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
